import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { X } from "lucide-react";
import { Stage, ContentItem, PainPoint, Opportunity } from '@/types/journey';
import EmotionSelector, { EmotionLevel } from './EmotionSelector';
import DynamicContentField from './stage/DynamicContentField';

interface StageEditorProps {
  stage: Stage;
  onClose: () => void;
  onUpdate: (stage: Stage) => void;
}

const StageEditor: React.FC<StageEditorProps> = ({ stage, onClose, onUpdate }) => {
  const [localStage, setLocalStage] = useState<Stage>({...stage});
  const modalRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    // Ensure contentItems exists
    if (!localStage.contentItems) {
      setLocalStage({
        ...localStage,
        contentItems: []
      });
    }
  }, [localStage]);
  
  // Close when clicking outside the editor
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);
  
  // Handle ESC key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);
  
  // Handle any field change
  const handleChange = (field: keyof Stage, value: any) => {
    setLocalStage({
      ...localStage,
      [field]: value
    });
  };
  
  // Handle content fields update
  const handleContentUpdate = (contentItems: ContentItem[]) => {
    setLocalStage({
      ...localStage,
      contentItems
    });
    
    // Update legacy fields for backward compatibility
    const painPointsItem = contentItems.find(item => item.type === 'painPoints');
    const opportunitiesItem = contentItems.find(item => item.type === 'opportunities');
    
    if (painPointsItem || opportunitiesItem) {
      setLocalStage(prev => {
        // For pain points: if it's an array of objects, convert to string for legacy support
        const painPointsString = painPointsItem && Array.isArray(painPointsItem.content) 
          ? painPointsItem.content.map((pp: PainPoint) => pp.text).join('\n')
          : painPointsItem?.content || prev.painPoints;
        
        // For opportunities: if it's an array of objects, convert to string for legacy support
        const opportunitiesString = opportunitiesItem && Array.isArray(opportunitiesItem.content)
          ? opportunitiesItem.content.map((opp: Opportunity) => opp.text).join('\n')
          : opportunitiesItem?.content || prev.opportunities;
          
        return {
          ...prev,
          painPoints: painPointsString,
          opportunities: opportunitiesString
        };
      });
    }
  };
  
  const handleSave = () => {
    onUpdate(localStage);
    onClose();
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <Card className="w-full max-w-md max-h-[calc(100vh-4rem)] overflow-y-auto" ref={modalRef}>
        <CardHeader className="flex flex-row items-center justify-between sticky top-0 bg-card z-10 pb-2">
          <CardTitle className="text-lg">Edit Stage</CardTitle>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-8 w-8"
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-4 pt-0">
          <div>
            <label htmlFor="title" className="text-sm font-medium mb-1.5 block">
              Stage Title
            </label>
            <Input
              id="title"
              value={localStage.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="Stage title"
            />
          </div>
          
          <div>
            <label htmlFor="emotion" className="text-sm font-medium mb-1.5 block">
              User Emotion
            </label>
            <EmotionSelector
              value={localStage.emotion as EmotionLevel}
              onChange={(value) => handleChange('emotion', value)}
              className="mt-1"
            />
          </div>
          
          <div>
            <label htmlFor="quote" className="text-sm font-medium mb-1.5 block">
              User Quote
            </label>
            <Textarea
              id="quote"
              value={localStage.quote}
              onChange={(e) => handleChange('quote', e.target.value)}
              placeholder="E.g., 'I'm not sure where to start...' or 'This seems complicated...'"
              className="resize-none whitespace-pre-wrap overflow-wrap-break-word"
              rows={2}
            />
          </div>
          
          <div>
            <label className="text-sm font-medium mb-1.5 block">
              Stage Content
            </label>
            <DynamicContentField
              contentItems={localStage.contentItems || []}
              onContentUpdate={handleContentUpdate}
            />
          </div>
          
          <div className="flex justify-end space-x-2 pt-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave}>
              Save Changes
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StageEditor;
